(() => {
  if (window.__eusariAccessibilityLoaded) return;
  window.__eusariAccessibilityLoaded = true;

  const storageKey = 'eusari.a11y.v1';
  const defaults = { version: 1, text: 'default', contrast: 'default', motion: 'system', spacing: 'default' };
  const allowed = {
    text: ['default', 'large', 'extra-large'],
    contrast: ['default', 'high'],
    motion: ['system', 'reduced'],
    spacing: ['default', 'comfortable'],
  };
  let state = { ...defaults };
  let panel = null;
  let launcher = null;
  let lastFocused = null;

  const isValidState = (candidate) => candidate
    && candidate.version === defaults.version
    && Object.keys(candidate).length === Object.keys(defaults).length
    && Object.entries(allowed).every(([key, values]) => values.includes(candidate[key]));

  const readState = () => {
    try {
      const candidate = JSON.parse(window.localStorage.getItem(storageKey));
      return isValidState(candidate) ? candidate : null;
    } catch (error) {
      return null;
    }
  };

  const safeWrite = () => {
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(state));
    } catch (error) {
      // Preferences remain active for the current session when storage is unavailable.
    }
  };

  const notify = () => {
    document.dispatchEvent(new CustomEvent('eusari:a11ychange', { detail: { ...state } }));
  };

  const applyState = () => {
    const root = document.documentElement;
    root.setAttribute('data-a11y-text', state.text);
    root.setAttribute('data-a11y-contrast', state.contrast);
    root.setAttribute('data-a11y-motion', state.motion);
    root.setAttribute('data-a11y-spacing', state.spacing);
  };

  const syncControls = () => {
    if (!panel) return;
    panel.querySelectorAll('[data-a11y-preference]').forEach((control) => {
      const preference = control.dataset.a11yPreference;
      control.checked = preference === 'spacing'
        ? state.spacing === 'comfortable'
        : control.value === state[preference];
    });
  };

  const commit = () => {
    applyState();
    syncControls();
    safeWrite();
    notify();
  };

  const mountLauncher = () => {
    const host = document.querySelector('[data-a11y-launcher-host]');
    if (launcher && host && launcher.parentElement !== host) host.append(launcher);
  };

  const refresh = () => {
    panel = document.querySelector('[data-a11y-panel]');
    launcher = document.querySelector('[data-a11y-launcher]') || launcher || panel?.querySelector('[data-a11y-launcher]');
    mountLauncher();
    applyState();
    syncControls();
  };

  const close = ({ restoreFocus = false } = {}) => {
    const dialog = panel?.querySelector('[data-a11y-dialog]');
    if (!dialog || dialog.hidden) return;
    dialog.hidden = true;
    launcher?.setAttribute('aria-expanded', 'false');
    if (restoreFocus && lastFocused?.isConnected) lastFocused.focus();
    lastFocused = null;
  };

  const open = (launcher) => {
    const dialog = panel?.querySelector('[data-a11y-dialog]');
    if (!dialog) return;
    lastFocused = launcher;
    dialog.hidden = false;
    launcher.setAttribute('aria-expanded', 'true');
    syncControls();
    dialog.querySelector('[data-a11y-preference]')?.focus();
  };

  const updatePreference = (control) => {
    const preference = control.dataset.a11yPreference;
    if (!allowed[preference]) return;
    state = {
      ...state,
      [preference]: preference === 'spacing' ? (control.checked ? 'comfortable' : 'default') : control.value,
    };
    commit();
  };

  const reset = () => {
    state = { ...defaults };
    commit();
    const status = panel?.querySelector('[data-a11y-status]');
    if (status) {
      status.textContent = '';
      window.setTimeout(() => {
        status.textContent = status.dataset.a11yResetCompleteLabel || '';
      }, 0);
    }
  };

  document.addEventListener('click', (event) => {
    const launcher = event.target.closest('[data-a11y-launcher]');
    if (launcher) {
      refresh();
      const dialog = panel?.querySelector('[data-a11y-dialog]');
      if (dialog?.hidden) open(launcher);
      else close({ restoreFocus: true });
      return;
    }

    if (!panel) return;
    const control = event.target.closest('[data-a11y-preference]');
    if (control) {
      updatePreference(control);
      return;
    }
    if (event.target.closest('[data-a11y-reset]')) {
      reset();
      return;
    }
    if (event.target.closest('[data-a11y-close]')) {
      close({ restoreFocus: true });
      return;
    }
    if (!event.target.closest('[data-a11y-panel]')) close();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') close({ restoreFocus: true });
  });

  window.EusariAccessibility = Object.freeze({
    getState: () => ({ ...state }),
    setPreference: (preference, value) => {
      if (!allowed[preference] || !allowed[preference].includes(value)) return;
      state = { ...state, [preference]: value };
      commit();
    },
    reset,
  });

  const stored = readState();
  if (stored) state = stored;
  refresh();
  window.addEventListener('pageshow', refresh);
  window.addEventListener('storage', (event) => {
    if (event.key !== storageKey) return;
    const storedState = readState();
    if (!storedState) return;
    state = storedState;
    applyState();
    syncControls();
    notify();
  });
  document.addEventListener('shopify:section:load', refresh);
})();
